import { Component, effect, ElementRef, Inject, OnDestroy, OnInit, PLATFORM_ID, signal, ViewChild, DOCUMENT } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ChatMessage, Message } from '../../core/service/socket/interfaces/message.interface';
import { SocketService } from '../../core/service/socket/socket.service';
import { AuthService } from '../../core/service/auth.service';
import { CreateChatService } from './service/create-chat.service';
import { Subscription } from 'rxjs';
import { AuthUserResponse } from './interfaces/chat-call-center.interface';
import { FormsModule } from '@angular/forms';
import { UploadService, ImageUploadResponse } from '../../core/service/upload/upload.service';
import { environment } from '../../../environments/environment';
import { CatalogoProblemasComponent } from "./components/catalogo-problemas/catalogo-problemas.component";
import { CreateChatCallCenterResponse } from './interfaces/create-chat-call-center.interface';
import { ChatActiveResponse } from './interfaces/active-chat.interface';
import { AcceptedChat } from './interfaces/accepted-chat.interface';
import {
  ChatMessage as ChatHistoryMessage,
  ChatHistoryData,
  JoinedChatData,
  ChatErrorData,
  UserReconnectedData
} from '../interfaces/chat-reconnection.interface';

@Component({
  selector: 'shared-chat-call-center',
  standalone: true,
  imports: [CommonModule, FormsModule, CatalogoProblemasComponent],
  templateUrl: './chat-call-center.component.html',
  styleUrl: './chat-call-center.component.css',

})
export class ChatCallCenterComponent implements OnInit, OnDestroy {
  messages: ChatMessage[] = [];
  newMessage: string = '';
  isConnected: boolean = false;
  isBrowser: boolean = false;
  isAuthenticated: boolean = false;
  username: string = 'Anónimo';
  chatOpen = false;
  currentChatId: string = '';
  userId: number | null = null;
  origin: string = 'client';
  currentTime: string = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  selectedFile: File | null = null;
  selectedFilePreviewUrl: string | null = null;
  isUploading: boolean = false;
  selectedProblem = signal<string | null>(null);
  uploadProgress: number = 0;
  showWelcome: boolean = true;
  messageEndChat = signal<string>('');
  messageAcceptedChat = signal<string>('');
  isTyping: boolean = false;
  otherUserTyping: boolean = false;
  typingTimeout: any = null;
  typingUsername: string = '';
  chatEnded: boolean = false;
  @ViewChild('messageEnd')
  private messageEndChatElement?: ElementRef<HTMLDivElement>;
  private connectionStatusSubscription?: Subscription;
  private authSubscription?: Subscription;
  private chatActiveSubscription?: Subscription;

  constructor(
    private socketService: SocketService,
    private authService: AuthService,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private document: Document,
    private createChatService: CreateChatService,
    private uploadService: UploadService
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    effect(() => {

      this.socketService.onE('chat_accepted').subscribe((data: AcceptedChat) => {
        this.messageAcceptedChat.set(`Chat con el agente ${data.agentName} iniciado`);
        const currentTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        const confirmationMessage: ChatMessage = {
          message: this.messageAcceptedChat(),
          username: data.agentName,
          time: currentTime,
          origin: 'system',
          content: 'text'
        };

        // Agregar el mensaje a la lista con un pequeño delay para mejor UX
        setTimeout(() => {
          this.messages.push(confirmationMessage);
        }, 500);


      })

      this.socketService.onE('messageReceived').subscribe((data: Message) => {
        const normalizeUrl = (raw?: string) => {
          if (!raw) return undefined;
          return raw.startsWith('http') ? raw : `${environment.uploadPath}${raw.startsWith('/') ? '' : '/'}${raw}`;
        };
        const basename = (p?: string) => p ? (p.split('/').pop() || p.split('\\').pop() || p) : undefined;

        const normalizedTop = normalizeUrl(data.fileUrl);
        const fallbackUrl = data.fileName ? `${environment.uploadPath}/image-loader/download/${basename(data.fileName)}` : undefined;

        const messae: ChatMessage = {
          message: data.content || '',
          username: data.senderUsername,
          time: '',
          origin: 'agent',
          content: data.type === 'IMAGE' ? 'img' : 'text',
          fileUrl: data.type === 'IMAGE' ? (normalizedTop || fallbackUrl) : undefined,
          fileName: data.fileName,
          fileSize: data.fileSize,
          mimeType: data.mimeType
        };
        this.messages.push(messae);
      })

      this.socketService.onE('chat-terminado').subscribe((data) => {
        this.chatEnded = true;
        this.messageEndChat.set('Este chat ha finalizado');
        // console.log(this.messageEndChat());

        // Agregar mensaje de finalización al chat
        const currentTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        const endMessage: ChatMessage = {
          message: 'Este chat ha finalizado. Ya no puedes enviar más mensajes.',
          username: 'Sistema',
          time: currentTime,
          origin: 'system',
          content: 'text'
        };
        this.messages.push(endMessage);

        if (this.messageEndChatElement) { // Check if it exists
          this.messageEndChatElement.nativeElement.textContent = this.messageEndChat();
        }
      });
    });

    // Escuchar eventos de typing
    this.socketService.onE('userTyping').subscribe((data: { userId: number; isTyping: boolean; username?: string }) => {
      // console.log('userTyping', data);
      // Solo mostrar typing si no es el usuario actual
      if (data.userId !== this.userId) {
        this.otherUserTyping = data.isTyping;
        this.typingUsername = data.username || 'Alguien';
      }
    })
  }
  // Método para alternar la visibilidad del chat
  toggleChat(): void {
    this.chatOpen = !this.chatOpen;

    // Solo verificar/crear chat cuando se abre el chat
    if (this.chatOpen && this.isAuthenticated) {
      this.initializeChatSession();
    }

    // Actualizar la hora actual
    this.currentTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  ngOnInit(): void {
    if (this.isBrowser) {
      // Verificar si el usuario está autenticado
      this.isAuthenticated = this.authService.isLoggedIn();

      // Obtener información del usuario si está autenticado
      if (this.isAuthenticated) {
        const userData = this.authService.getCurrentUser();
        this.authSubscription = userData.subscribe(user => {
          if (user) {
            this.userId = user.ua_id;
            this.username = `${user.ua_name} ${user.ua_lastname}`.trim();
          }
        });
      }

      // Inicializar el chat
      this.initializeChat();
    }
  }


  private initializeChat(): void {
    this.socketService.connect();


    // this.socketService.onChatMessage((messageData: ChatMessage) => {
    //   // Si el mensaje es válido, lo agregamos a la lista
    //   if (messageData && (messageData.message || messageData.content === 'img') && messageData.username) {
    //     // Asegurarse de que el nombre de usuario esté correcto para mensajes propios
    //     if (messageData.username === this.username) {
    //       messageData.username = this.username;
    //     }
    //     this.messages.push(messageData);
    //   }
    // });
  }

  private createNewChat(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.selectedProblem()) {
        const error = 'No se puede crear un chat sin seleccionar un problema';
        console.error(error);
        reject(new Error(error));
        return;
      }

      this.createChatService.createChat(this.selectedProblem()!).pipe(

      ).subscribe({
        next: (response: CreateChatCallCenterResponse) => {
          // console.log('Chat creado correctamente', response);
          // El backend devuelve la estructura: {success: true, data: chatObject}
          // El _id está dentro de response.data
          if (response.data._id) {
            this.currentChatId = response.data._id;
            this.createChatService.setIdChatSessionStorage(response.data._id);
            this.socketService.joinRoomSoporte(Number(response.data.clientId), response.data.clientName);
            // this.socketService.setCurrentChatId(this.currentChatId);
            this.showWelcome = false;
            this.chatEnded = false;
            resolve();
          } else {
            const error = 'No se pudo obtener el ID del chat creado';
            console.error('Error:', error, response);
            reject(new Error(error));
          }
        },
        error: (error) => {
          console.error('Error al crear el chat:', error);
          reject(error);
        }
      });
    });
  }

  private initializeChatSession(): void {
    if (!this.isAuthenticated) return;

    // Verificar si hay un chat ID en sessionStorage
    const storedChatId = this.getStoredChatId(); //

    if (storedChatId) {
      // Si hay un chat ID almacenado, verificar si está activo y reconectar
      this.checkActiveChat();
    } else {
      // Si no hay chat ID, mostrar bienvenida para crear uno nuevo
      this.showWelcome = true;
    }
  }

  private getStoredChatId(): string | null {
    try {
      const chatId = sessionStorage.getItem('idChat');
      if (chatId && chatId !== 'null' && chatId !== 'undefined') {
        this.socketService.joinChat(this.currentChatId);
        // Verificar si el valor es un JSON válido
        try {
          return JSON.parse(chatId);
        } catch (parseError) {
          // Si no es JSON válido, asumir que es un string directo
          console.warn('Chat ID no es JSON válido, usando como string directo:', chatId);
          return chatId;
        }
      }
    } catch (error) {
      console.error('Error al obtener chat ID del sessionStorage:', error);
    }
    return null;
  }

  private checkActiveChat(): void {
    if (!this.isAuthenticated) return;

    try {
      this.chatActiveSubscription = this.createChatService.getChatActive().pipe().subscribe({
        next: (response: ChatActiveResponse) => {
          if (response.data && (response.data.status === 'pending' || response.data.status === 'active')) {
            // Chat activo encontrado
            this.currentChatId = response.data._id;
            this.showWelcome = false;
            this.chatEnded = false;

            // Asegurar que el ID esté actualizado en sessionStorage
            this.createChatService.setIdChatSessionStorage(this.currentChatId);

            // Reconectar automáticamente a la sala de chat
            this.reconnectToChatRoom();
          } else {
            // No hay chat activo, limpiar sessionStorage y mostrar bienvenida
            this.clearChatSession();
            this.showWelcome = true;
          }
        },
        error: (error) => {
          console.error('Error al obtener chats activos:', error);
          // En caso de error, mostrar bienvenida para crear nuevo chat
          this.clearChatSession();
          this.showWelcome = true;
        }
      });
    } catch (error) {
      console.error('Error al verificar chat activo:', error);
      // Si no hay chat ID disponible, mostrar bienvenida
      this.clearChatSession();
      this.showWelcome = true;
    }
  }

  // Método para reconectar automáticamente a la sala de chat
  private reconnectToChatRoom(): void {
    if (!this.currentChatId || !this.userId || !this.username) {
      console.error('Datos insuficientes para reconectar al chat');
      return;
    }

    // console.log('Reconectando a la sala de chat:', this.currentChatId);

    // Usar el nuevo método de reconexión del socket service
    this.socketService.reconnectToActiveChat(this.currentChatId, this.userId, this.username);

    // Escuchar eventos de reconexión
    this.setupReconnectionListeners();
  }

  // Configurar listeners para los eventos de reconexión
  private setupReconnectionListeners(): void {
    // Listener para confirmación de unión al chat
    this.socketService.onJoinedChat().subscribe((data: JoinedChatData) => {
      // console.log('Reconectado exitosamente al chat:', data);
    });

    // Listener para errores al unirse al chat
    this.socketService.onJoinChatError().subscribe((error: ChatErrorData) => {
      console.error('Error al reconectar al chat:', error);
      // Si hay error, limpiar sesión y mostrar bienvenida
      this.clearChatSession();
      this.showWelcome = true;
    });

    // Listener para recibir el historial del chat
    this.socketService.onChatHistory().subscribe((data: ChatHistoryData) => {
      // console.log('Historial del chat recibido:', data);

      // Cargar mensajes del historial
      if (data.messages && Array.isArray(data.messages)) {
        this.loadChatHistory(data.messages);
      }
    });

    // Listener para errores al obtener historial
    this.socketService.onChatHistoryError().subscribe((error: ChatErrorData) => {
      console.error('Error al obtener historial del chat:', error);
    });

    // Listener para notificaciones de reconexión de otros usuarios
    this.socketService.onUserReconnected().subscribe((data: UserReconnectedData) => {
      // console.log('Usuario reconectado al chat:', data);
    });
  }

  // Método para cargar el historial de mensajes
  private loadChatHistory(historyMessages: ChatHistoryMessage[]): void {
    const normalizeUrl = (raw?: string) => {
      if (!raw) return undefined;
      return raw.startsWith('http') ? raw : `${environment.uploadPath}${raw.startsWith('/') ? '' : '/'}${raw}`;
    };
    const chatMessages: ChatMessage[] = historyMessages.map(msg => {
      return {
        message: msg.content || '',
        username: msg.sender === 'client' ? this.username : (msg.sender || 'Agente'),
        time: msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '',
        origin: msg.sender === 'client' ? 'client' : 'agent',
        content: msg.type === 'image' ? 'img' : 'text',
        fileUrl: (() => {
          const normalized = normalizeUrl(msg.metadata?.fileUrl);
          if (normalized) return normalized;
          if (msg.metadata?.fileName) {
            const base = msg.metadata.fileName.split('/').pop() || msg.metadata.fileName.split('\\').pop() || msg.metadata.fileName;
            return `${environment.uploadPath}/image-loader/download/${base}`;
          }
          return undefined;
        })(),
        fileName: msg.metadata?.fileName,
        fileSize: msg.metadata?.fileSize,
        mimeType: msg.metadata?.mimeType
      };
    });

    // Reemplazar los mensajes actuales con el historial
    this.messages = chatMessages;
    // console.log('Historial de mensajes cargado:', this.messages.length, 'mensajes');
  }

  private clearChatSession(): void {
    sessionStorage.removeItem('idChat');
    this.currentChatId = '';
    this.messages = [];
    this.chatEnded = false;
  }

  sendMessage(): void {
    if (this.selectedFile) {
      this.sendFileMessage();
    } else if (this.newMessage.trim() && this.isAuthenticated) {
      const messageText = this.newMessage.trim();
      const currentTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

      if (!this.username || this.username === 'Anónimo') {
        const userData = this.authService.getCurrentUser() as AuthUserResponse;
        if (userData?.data?.user) {
          this.username = `${userData.data.user.ua_name} ${userData.data.user.ua_lastname}`.trim();
        }
      }

      const message: Message = {
        senderId: this.authService.getUser()?.ua_id!,
        senderUsername: this.username,
        chatId: sessionStorage.getItem('idChat')!,
        content: messageText,
        type: 'TEXT',
        fileName: '',
        fileUrl: '',
        fileSize: 0,
        timestamp: new Date(),

      }
      this.socketService.sendMessageSupport(message);
      const messageN: ChatMessage = {
        message: messageText,
        username: this.username,
        time: currentTime,
        content: 'text'
      };
      this.messages.push(messageN);

      this.newMessage = '';
      // Detener indicador de typing al enviar mensaje
      this.stopTyping();
    }
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      // Validar tamaño del archivo (máximo 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('El archivo es demasiado grande. El tamaño máximo es 10MB.');
        return;
      }

      // Validar tipo de archivo
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        alert('Tipo de archivo no permitido. Solo se permiten imágenes (JPEG, PNG, GIF, WebP).');
        return;
      }

      this.selectedFile = file;

      // Generar URL de vista previa para imágenes
      if (this.isImageFile(file)) {
        this.selectedFilePreviewUrl = URL.createObjectURL(file);
      }
    }
  }

  removeSelectedFile(): void {
    if (this.selectedFilePreviewUrl) {
      URL.revokeObjectURL(this.selectedFilePreviewUrl);
      this.selectedFilePreviewUrl = null;
    }
    this.selectedFile = null;
  }

  private sendFileMessage(): void {
    if (!this.currentChatId || !this.selectedFile) return;

    this.isUploading = true;
    this.uploadProgress = 0;

    this.uploadService.uploadImage(this.selectedFile).subscribe({
      next: (response: ImageUploadResponse) => {
        const currentTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

        const basename = (p?: string) => p ? (p.split('/').pop() || p.split('\\').pop() || p) : '';
        const filePath = response?.data?.filePath || '';
        const fileSize = response?.data?.fileSize || 0;
        const fileType = response?.data?.fileType || '';
        const imageUrl = `${environment.uploadPath}/image-loader/download/${basename(filePath)}`;

        // Crear mensaje para enviar al backend
        const message: Message = {
          senderId: this.authService.getUser()?.ua_id!,
          senderUsername: this.username,
          chatId: sessionStorage.getItem('idChat')!,
          content: this.newMessage.trim() || '',
          type: 'IMAGE',
          fileName: filePath,
          fileUrl: imageUrl,
          fileSize: fileSize,
          mimeType: fileType,
          timestamp: new Date()
        };

        // Enviar mensaje al backend
        this.socketService.sendMessageSupport(message);

        // Agregar mensaje a la UI local
        const messageN: ChatMessage = {
          message: this.newMessage.trim() || '',
          username: this.username,
          time: currentTime,
          content: 'img',
          fileUrl: imageUrl,
          fileName: filePath,
          fileSize: fileSize,
          mimeType: fileType
        };
        this.messages.push(messageN);

        // Limpiar formulario
        this.newMessage = '';
        // Detener indicador de typing al enviar archivo
        this.stopTyping();
        if (this.selectedFilePreviewUrl) {
          URL.revokeObjectURL(this.selectedFilePreviewUrl);
          this.selectedFilePreviewUrl = null;
        }
        this.selectedFile = null;
        this.isUploading = false;
        this.uploadProgress = 0;
      },
      error: (error) => {
        console.error('Error al subir archivo:', error);
        alert('Error al subir el archivo. Inténtalo de nuevo.');
        this.isUploading = false;
        this.uploadProgress = 0;
      }
    });
  }

  isImageFile(file: File): boolean {
    return file.type.startsWith('image/');
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  openImagePreview(imageUrl: string): void {
    window.open(imageUrl, '_blank');
  }

  getFileNameOnly(fileName: string): string {
    if (!fileName) return '';
    // Extraer solo el nombre del archivo sin la ruta
    return fileName.split('/').pop() || fileName;
  }
  ngOnDestroy(): void {
    // Limpiar todas las suscripciones
    if (this.connectionStatusSubscription) {
      this.connectionStatusSubscription.unsubscribe();

    }

    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }

    if (this.chatActiveSubscription) {
      this.chatActiveSubscription.unsubscribe();
    }

    // Limpiar timeout de typing
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }

    // Detener typing al destruir el componente
    this.stopTyping();
  }

  checkDisabled(): boolean {
    // Si el chat ha terminado, deshabilitar todo
    if (this.chatEnded) {
      return true;
    }

    const problem = this.selectedProblem();

    // Si showWelcome es false (chat activo), solo verificar si está subiendo
    if (!this.showWelcome) {
      return this.isUploading;
    }

    // Si showWelcome es true (esperando selección de problema), verificar que haya problema seleccionado
    const disabled = this.isUploading || !problem || problem.trim() === '';

    return disabled;
  }
  setSelectProblem($event: string): void {
    this.selectedProblem.set($event);

    // Crear el chat inmediatamente después de seleccionar el problema
    this.createNewChat().then(() => {
      // Agregar mensaje automático después de crear el chat
      const currentTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      const confirmationMessage: ChatMessage = {
        message: `Perfecto, has seleccionado: "${$event}". Un agente especializado se pondrá en contacto contigo pronto para ayudarte con este tema. Mientras tanto, puedes escribir más detalles sobre tu consulta.`,
        username: 'Asistente Virtual',
        time: currentTime,
        origin: 'system',
        content: 'text'
      };

      // Agregar el mensaje a la lista con un pequeño delay para mejor UX
      setTimeout(() => {
        this.messages.push(confirmationMessage);
      }, 500);
    }).catch((error) => {
      console.error('Error al crear el chat:', error);
    });
  }

  // Métodos para manejar typing
  onInputChange(): void {
    if (!this.currentChatId || !this.userId) return;

    // Si no estaba escribiendo, notificar que empezó a escribir
    if (!this.isTyping) {
      this.isTyping = true;
      this.socketService.emit('typing', {
        senderId: this.userId,
        receiverId: this.currentChatId,
        isTyping: true
      });
    }

    // Limpiar timeout anterior
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }

    // Establecer nuevo timeout para dejar de escribir
    this.typingTimeout = setTimeout(() => {
      this.stopTyping();
    }, 2000); // Dejar de mostrar "escribiendo" después de 2 segundos sin actividad
  }

  stopTyping(): void {
    if (this.isTyping && this.currentChatId && this.userId) {
      this.isTyping = false;
      this.socketService.emit('typing', {
        senderId: this.userId,
        receiverId: this.currentChatId,
        isTyping: false
      });
    }

    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
      this.typingTimeout = null;
    }
  }

}
